import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { translateDomain } from 'utils/labels_utils'

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 10px;
`

const SectionLabelSpaced = styled(SectionLabel)`
  margin: 0 0 12px;
`

const FormulaBox = styled.div`
  background: var(--vo-mist);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
`

const FormulaLine = styled.p`
  font-family: monospace;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0 0 4px;
`

const FormulaResult = styled.p`
  font-family: monospace;
  font-size: 16px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  margin: 8px 0 0;
`

const DomainsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`

const DomainNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`

const DomainName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`

const DomainPct = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`

const ProgressTrack = styled.div`
  height: 8px;
  background: var(--vo-cloud);
  border-radius: 4px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  width: ${({ $pct }) => $pct || 0}%;
`

const DeficienciesWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const DeficiencyCard = styled.div`
  border: 1px solid var(--vo-treatment-share-border);
  background: var(--vo-treatment-share-bg);
  border-radius: 10px;
  padding: 14px 16px;
`

const DeficiencyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const DeficiencyName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0;
`

const DeficiencyDetails = styled.div`
  display: flex;
  gap: 16px;
`

const DetailItem = styled.div``

const DetailItemLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  color: var(--vo-slate);
  margin: 0 0 2px;
`

const DetailItemValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $gap }) => $gap ? 'var(--vo-danger)' : 'var(--vo-ink-2)'};
  margin: 0;
`

const SuccessBox = styled.div`
  background: var(--vo-success-10);
  border-radius: 10px;
  padding: 12px 16px;
`

const SuccessText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-success);
  margin: 0;
`

const WarningSVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--vo-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const getDomainColor = (pct) =>
  pct >= 90 ? 'var(--vo-success)' : pct >= 70 ? 'var(--vo-warning)' : 'var(--vo-danger)'

const getScoreColor = (pct) =>
  pct >= 90 ? 'var(--vo-success)' : pct >= 70 ? 'var(--vo-warning)' : 'var(--vo-danger)'

export const TrustScoreDetail = ({ trustScore }) => {
  const { t } = useTranslation(['gap-analysis'])

  if (!trustScore) {
    return (
      <SuccessBox>
        <SuccessText>{t('gap-analysis:assessment.trustScore.noScore')}</SuccessText>
      </SuccessBox>
    )
  }

  const { totalActualPoints, totalPossiblePoints, percentage, criticalDeficiencies, domainScores } = trustScore
  const scoreColor = getScoreColor(percentage)

  return (
    <div>
      <SectionLabel>{t('gap-analysis:assessment.trustScore.formula')}</SectionLabel>
      <FormulaBox>
        <FormulaLine>{t('gap-analysis:assessment.trustScore.formulaLine1')}</FormulaLine>
        <FormulaLine>{t('gap-analysis:assessment.trustScore.formulaLine2', { actual: totalActualPoints, possible: totalPossiblePoints })}</FormulaLine>
        <FormulaResult $color={scoreColor}>{`= ${percentage}%`}</FormulaResult>
      </FormulaBox>

      {domainScores && domainScores.length > 0 && (
        <>
          <SectionLabelSpaced>{t('gap-analysis:assessment.trustScore.performanceByDomain')}</SectionLabelSpaced>
          <DomainsWrap>
            {domainScores.map((domain, idx) => {
              const color = getDomainColor(domain.percentage)
              return (
                <div key={idx}>
                  <DomainNameRow>
                    <DomainName>{translateDomain(t, domain.domain)}</DomainName>
                    <DomainPct $color={color}>{domain.percentage.toFixed(1)}{'%'}</DomainPct>
                  </DomainNameRow>
                  <ProgressTrack>
                    <ProgressFill $color={color} $pct={domain.percentage} />
                  </ProgressTrack>
                </div>
              )
            })}
          </DomainsWrap>
        </>
      )}

      <SectionLabelSpaced>
        {t('gap-analysis:assessment.trustScore.criticalDeficiencies')}
        {' ('}
        {criticalDeficiencies?.length || 0}
        {')'}
      </SectionLabelSpaced>

      {!criticalDeficiencies || criticalDeficiencies.length === 0 ? (
        <SuccessBox>
          <SuccessText>{t('gap-analysis:assessment.trustScore.noCriticalDeficiencies')}</SuccessText>
        </SuccessBox>
      ) : (
        <DeficienciesWrap>
          {criticalDeficiencies.map((deficiency, idx) => (
            <DeficiencyCard key={idx}>
              <DeficiencyHeader>
                <WarningSVG />
                <DeficiencyName>{str(deficiency.controlDescription)}</DeficiencyName>
              </DeficiencyHeader>
              <DeficiencyDetails>
                <DetailItem>
                  <DetailItemLabel>{t('gap-analysis:assessment.trustScore.targetMaturity')}</DetailItemLabel>
                  <DetailItemValue>{'L'}{deficiency.targetMaturity}</DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>{t('gap-analysis:assessment.trustScore.vendorMaturity')}</DetailItemLabel>
                  <DetailItemValue>{'L'}{deficiency.vendorMaturity}</DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>{t('gap-analysis:assessment.trustScore.gap')}</DetailItemLabel>
                  <DetailItemValue $gap>{t('gap-analysis:assessment.trustScore.gapBelowTarget', { count: deficiency.gap })}</DetailItemValue>
                </DetailItem>
              </DeficiencyDetails>
            </DeficiencyCard>
          ))}
        </DeficienciesWrap>
      )}
    </div>
  )
}
